import React, { useEffect, useMemo, useState } from "react";

import { Building2, Mail, Search } from "lucide-react";

import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Spinner } from "../../components/ui/spinner";
import { useWorkspaces } from "../hooks";
import { setActiveWorkspaceId } from "../utils/workspaceStorage";

import type { Workspace } from "../types";

interface WorkspaceSwitcherProps {
  isOpen: boolean;
  onClose?: () => void;
  disableClose?: boolean;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  isOpen,
  onClose,
  disableClose = false,
}) => {
  const { data: workspacesData, isLoading, error } = useWorkspaces();
  const [searchQuery, setSearchQuery] = useState("");

  const handleWorkspaceSelect = (workspace: Workspace) => {
    if (workspace.active) return;
    setActiveWorkspaceId(workspace.id);
    window.location.href = import.meta.env.BASE_URL;
  };

  const handleOpenChange = (open: boolean) => {
    if (!disableClose && !open && onClose) {
      onClose();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredWorkspaces = useMemo(() => {
    if (!workspacesData?.workspaces) return [];
    if (!searchQuery.trim()) return workspacesData.workspaces;

    const query = searchQuery.toLowerCase();
    return workspacesData.workspaces.filter(
      (workspace) =>
        workspace.name.toLowerCase().includes(query) ||
        workspace.domain?.toLowerCase().includes(query),
    );
  }, [workspacesData?.workspaces, searchQuery]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/60 backdrop-blur-sm" />
        <DialogContent className="!max-w-3xl !w-[calc(100vw-2rem)] sm:!w-[calc(100vw-4rem)] p-0 gap-0 border-border">
          <div className="w-full max-h-[85vh] overflow-hidden rounded-lg">
            <div className="p-6 border-b border-border bg-primary/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary bg-[image:var(--primary-gradient-avatar,none)] flex items-center justify-center">
                  <Building2 className="text-primary-foreground" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    {disableClose
                      ? "Select Your Workspace"
                      : "Switch Workspace"}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {disableClose
                      ? "Choose a workspace to get started"
                      : "Select a workspace to continue working"}
                  </p>
                </div>
              </div>
            </div>

            {!isLoading &&
              !error &&
              workspacesData?.workspaces &&
              workspacesData.workspaces.length > 3 && (
                <div className="px-6 py-3 border-b border-border">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      size={16}
                    />
                    <Input
                      type="text"
                      placeholder="Search workspaces..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  {searchQuery && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Showing {filteredWorkspaces.length} of{" "}
                      {workspacesData.workspaces.length} workspaces
                    </p>
                  )}
                </div>
              )}

            <div className="p-6 h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Spinner size="lg" />
                  <p className="text-sm text-muted-foreground mt-4">
                    Loading workspaces...
                  </p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="text-destructive text-center">
                    <p className="font-semibold mb-2">
                      Error loading workspaces
                    </p>
                    <p className="text-sm">Please try again later</p>
                  </div>
                </div>
              ) : !workspacesData?.workspaces?.length ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Building2
                    size={48}
                    className="text-muted-foreground/30 mb-4"
                  />
                  <p className="text-muted-foreground">
                    No workspaces available
                  </p>
                </div>
              ) : filteredWorkspaces.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Search size={48} className="text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium mb-1">
                    No workspaces found
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Try a different search term
                  </p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {filteredWorkspaces.map((workspace) => (
                    <button
                      key={workspace.id}
                      onClick={() => handleWorkspaceSelect(workspace)}
                      disabled={workspace.active}
                      className={`group relative w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        workspace.active
                          ? "border-primary bg-primary/5 cursor-default"
                          : "border-border hover:border-primary/50 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 text-primary-foreground font-semibold text-lg ${
                            workspace.active
                              ? "bg-primary"
                              : "bg-muted-foreground/50 group-hover:bg-primary"
                          } transition-all duration-200`}
                        >
                          {getInitials(workspace.name)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg text-foreground truncate">
                              {workspace.name}
                            </h3>
                            {workspace.status === "invited" && (
                              <Badge
                                variant="secondary"
                                className="flex items-center gap-1 text-xs"
                              >
                                <Mail size={12} />
                                Invited
                              </Badge>
                            )}
                          </div>
                          {workspace.domain && (
                            <p className="text-sm text-muted-foreground truncate">
                              {workspace.domain}
                            </p>
                          )}
                        </div>
                      </div>
                      {workspace.active && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-primary hover:bg-primary text-primary-foreground">
                            Current
                          </Badge>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
